import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  useContainerSelector,
  useWidgetSelectorForType
} from '../../components/widgets/core/hooks'
import { WidgetsContext } from '../../components/widgets/core/context'
import { Widgets } from '../../components/widgets/core'
import Portal from '../../components/Portal'

import { HOTSPOT } from '../../constants/widgetTypes'
import delay from 'lodash/delay';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';


export const HotspotContext = React.createContext({
  active: null,
  exit: true,
  onMouseOver: () => {},
  onMouseOut: () => {},
  onClick: () => {}
})

export const HotspotRender = ({ breakpoint, children }) => {
  const [state, setState] = useState({
    active: null,
    parent: null,
    exit: false
  })
  let debouncedFunctionRef = null
  const stateRef = useRef(state)
  const context = React.useContext(WidgetsContext)

  const hotspotWidgets = useWidgetSelectorForType(HOTSPOT)

  useEffect(() => {
    if (!context.development) {
      window.addEventListener('click', hideHotspotForClick, true)

      return () => {
        window.removeEventListener('click', hideHotspotForClick, true)
      }
    }
  }, [])

  useEffect(() => {
    stateRef.current = state
  }, [state])

  const dynamicDebounce = (func, delay) => {
    let timeout

    const debounced = (...args) => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => {
        func.apply(this, args)
      }, delay)
    }

    debounced.cancel = () => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
    }

    return debounced
  }

  const cleanupAllHoverAttributes = () => {
    const allHoverElements = document.querySelectorAll('[data-hover-on]')
    allHoverElements.forEach(el => el.removeAttribute('data-hover-on'))
  }

  const handleMouseOver = (e, target, hotspot) => {
    var hotspotId = get(hotspot, 'uid', null)
    if (!hotspotId) {
      return
    }

    cleanupAllHoverAttributes()

    var hotspotElement = document.getElementById(hotspotId)
    var targetElement = document.getElementById(target.uid)

    hotspotElement?.setAttribute('data-hover-on', true)
    targetElement?.setAttribute('data-hover-on', true)

    if (debouncedFunctionRef) {
      debouncedFunctionRef.cancel();
    }

    setState((state) => {
      return {
        ...state,
        active: hotspotId,
        parent: target.uid,
        exit: false
      }
    })
  }

  const handleMouseOut = (e, target, hotspot) => {
    var hotspotId = get(hotspot, 'uid', null)
    var targetId = get(target, 'uid', null)

    if (!hotspotId) {
      return
    }

    const exitAnimation = get(
      hotspot,
      `properties.${breakpoint}.exitAnimation`,
      null
    )
    let debounceTime = 500

    if (exitAnimation) {
      debounceTime = (exitAnimation.delay + exitAnimation.duration) * 1000
    }

    var hotspotElement = document.getElementById(hotspotId)
    var targetElement = document.getElementById(targetId)
  
    if (hotspotElement?.contains(e.relatedTarget)) return;
  
    hotspotElement?.removeAttribute('data-hover-on')
    targetElement?.removeAttribute('data-hover-on')

    delay(
      () => {
        const currentHotspotElement = document.getElementById(hotspotId)
        if (
          currentHotspotElement &&
          !currentHotspotElement.hasAttribute('data-hover-on') &&
          exitAnimation
        ) {
          setState((state) => ({
            ...state,
            exit: true
          }))
        }
      },
      300,
      'later'
    )

    hideHotpsotForHover(debounceTime, hotspotId)
  }

  const handleClick = (e, target, hotspot) => {
    var hotspotId = get(hotspot, 'uid', null)
    var targetId = get(target, 'uid', null)

    cleanupAllHoverAttributes()

    var hotspotElement = document.getElementById(hotspotId)
    var targetElement = document.getElementById(targetId)

    hotspotElement?.setAttribute('data-hover-on', true)
    targetElement?.setAttribute('data-hover-on', true)

    if (debouncedFunctionRef) {
      debouncedFunctionRef.cancel();
    }

    setState((state) => {
      return {
        ...state,
        active: hotspotId,
        parent: target.uid,
        exit: false
      }
    })
  }

  const hideHotpsotForHover = (duration, currentHotspotId) => {
    // Cancel the previous debounce if it exists
    if (debouncedFunctionRef) {
      debouncedFunctionRef.cancel()
    }

    // Create a new debounced function
    debouncedFunctionRef = dynamicDebounce(() => {
      const currentHotspotElement = document.getElementById(currentHotspotId)
      if (!currentHotspotElement || !currentHotspotElement.hasAttribute('data-hover-on')) {
        hideHotspot()
      }
    }, duration)

    // Trigger the debounce
    debouncedFunctionRef()
  }

  const hideHotpsotOnClick = (duration, event) => {

    if (debouncedFunctionRef) {
      debouncedFunctionRef.cancel()
    }
  
    debouncedFunctionRef = dynamicDebounce(() => {
      var hotspotElement = document.getElementById(stateRef.current.active);
      var targetElement = document.getElementById(stateRef.current.parent);
      const isOutsideHotspot = hotspotElement && !hotspotElement.contains(event.target);
      const isOutsideTarget = (targetElement && !targetElement.contains(event.target) || targetElement === null)

      if (isOutsideHotspot && isOutsideTarget) {
        hotspotElement?.removeAttribute("data-hover-on");
        targetElement?.removeAttribute("data-hover-on");

        hideHotspot();
      }
    }, duration)

    // Trigger the debounce
    debouncedFunctionRef()
  };

  const hideHotspotForClick = (event) => {
    if (!stateRef.current.active) {
      return;
    }

    var hotspotWidget = hotspotWidgets.find(item => item.uid === stateRef.current.active && item.type === HOTSPOT);
    const exitAnimation = get(hotspotWidget, `properties.${breakpoint}.exitAnimation`, null);

    const debounceTime = exitAnimation ? (exitAnimation.delay + exitAnimation.duration) * 1000 : 0;

    var hotspotElement = document.getElementById(stateRef.current.active);
    var targetElement = document.getElementById(stateRef.current.parent);
    const isOutsideHotspot = hotspotElement && !hotspotElement.contains(event.target);
    const isOutsideTarget = (targetElement && !targetElement.contains(event.target) || targetElement === null)

    if (isOutsideHotspot && isOutsideTarget) {
      hotspotElement?.removeAttribute("data-hover-on");
      targetElement?.removeAttribute("data-hover-on");
      setState((state) => {
        return {
          ...state,
          exit: true
        }
      })

      hideHotpsotOnClick(debounceTime, event)
    }
  }


  const hideHotspot = () => {
    setState((state) => {
      return {
        ...state,
        active: null,
        parent: null
      }
    })
  }

  const hotspotContextValue = React.useMemo(
    () => ({
      active: state.active,
      exit: state.exit,
      onMouseOver: handleMouseOver,
      onMouseOut: handleMouseOut,
      onClick: handleClick
    }),
    [state.active, state.exit]
  )

  return (
    <HotspotContext.Provider value={hotspotContextValue}>
      {children}

      {(hotspotWidgets || []).map((item, index) => {
        return <Widgets key={index} widgetProps={item} breakpoint={breakpoint} />
      })}
    </HotspotContext.Provider>
  )
}

const HotspotTriggerComponent = ({
  widget,
  breakpoint,
  targetRef,
  children
}) => {
  const hotspotContext = React.useContext(HotspotContext)

  const widgetContext = React.useContext(WidgetsContext)
  const selector = widgetContext.selector

  var widgets = useSelector(
    (state) => get(state, `${selector}.widgets`) || []
  )
  var hotspotWidget = widgets.find(
    (item) => item.parent === widget.uid && item.type === HOTSPOT
  )
  const trigger = get(hotspotWidget, `properties.${breakpoint}.trigger`)

  useEffect(() => {
    if (hotspotWidget && trigger === 'hover') {
      targetRef.current.addEventListener('mouseover', handleMouseOver)
      targetRef.current.addEventListener('mouseout', handleMouseOut)
    }

    if (hotspotWidget && trigger === 'click') {
      targetRef.current.addEventListener('click', handleClick)
    }

    return () => {
      if (targetRef.current) {
        targetRef.current.removeEventListener('mouseover', handleMouseOver)
        targetRef.current.removeEventListener('mouseout', handleMouseOut)
      }
    }
  }, [])

  const handleMouseOver = (e) => {
    hotspotContext.onMouseOver(e, widget, hotspotWidget)
  }

  const handleMouseOut = (e) => {
    hotspotContext.onMouseOut(e, widget, hotspotWidget)
  }

  const handleClick = (e) => {
    hotspotContext.onClick(e, widget, hotspotWidget)
  }

  return children
}

const hotspotTriggerEquals = function (prevProps, nextProps) {
  const paths = ['widget', 'breakpoint', 'targetRef', 'children']
  const next = pick(nextProps, paths)
  const previous = pick(prevProps, paths)

  const equals = isEqual(next, previous)

  return equals
}

export const HotspotTrigger = React.memo(
  HotspotTriggerComponent,
  hotspotTriggerEquals
)
